import React from "react";
import { Box, Typography, Stack, Chip, useTheme, Divider } from "@mui/material";
import {
  CheckCircleRounded,
  AccessTimeRounded,
  CodeRounded,
  HistoryRounded,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import { getBaseEditorOptions } from "../../utils/programming/monacoEditorConfig";

/**
 * Simplified Post-Submission View for Programming Questions
 * Shows only submission status, key stats, and the final code in read-only mode.
 */
const ProgrammingPostSubmissionView = ({
  code,
  language,
  languageName,
  timeSpent,
  retakes,
  maxAttempts,
  isMobile = false,
}) => {
  const theme = useTheme();

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const isExhausted = retakes >= maxAttempts;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Top Header - Status & Stats */}
      <Box
        sx={{
          p: { base: 2, md: 3 },
          borderBottom: "1px solid #E2E8F0",
          bgcolor: "#F8FAFC",
        }}
      >
        <Stack
          direction={{ base: "column", lg: "row" }}
          spacing={3}
          alignItems={{ base: "flex-start", lg: "center" }}
          justifyContent="space-between"
        >
          {/* Status Badge */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                bgcolor: "#DCFCE7",
                borderRadius: "12px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #BBF7D0",
              }}
            >
              <CheckCircleRounded sx={{ color: "#16A34A", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  fontSize: "18px",
                  lineHeight: 1.2,
                }}
              >
                Answer Submitted
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#16A34A", fontWeight: 600 }}
              >
                Solution locked.
              </Typography>
            </Box>
          </Stack>

          {/* Metrics Section */}
          <Stack
            direction="row"
            spacing={{ base: 2, md: 4 }}
            flexWrap="wrap"
            useFlexGap
            sx={{ rowGap: 2 }}
          >
            {/* Time */}
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                color="#64748B"
              >
                <AccessTimeRounded sx={{ fontSize: 16 }} />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Time Spent
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={700} color="#334155">
                {formatTime(timeSpent)}
              </Typography>
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 24,
                alignSelf: "center",
                display: { base: "none", sm: "block" },
              }}
            />

            {/* Language */}
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                color="#64748B"
              >
                <CodeRounded sx={{ fontSize: 16 }} />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Language
                </Typography>
              </Stack>
              <Chip
                label={languageName}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "11px",
                  fontWeight: 700,
                  bgcolor: "#E2E8F0",
                  color: "#475569",
                  borderRadius: "6px",
                }}
              />
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 24,
                alignSelf: "center",
                display: { base: "none", sm: "block" },
              }}
            />

            {/* Attempts */}
            <Stack spacing={0.5}>
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
                color="#64748B"
              >
                <HistoryRounded sx={{ fontSize: 16 }} />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Attempts
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                fontWeight={700}
                color={isExhausted ? "error.main" : "#334155"}
              >
                {retakes} / {maxAttempts}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Code Editor Section - READ ONLY */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            py: 1.5,
            px: 3,
            borderBottom: "1px solid #F1F5F9",
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            Submitted Code View
          </Typography>
        </Box>
        <Box sx={{ flex: 1, position: "relative" }}>
          <Editor
            height="100%"
            language={language}
            value={code}
            theme="vs-light"
            options={{
              ...getBaseEditorOptions(),
              readOnly: true,
              minimap: { enabled: false },
              padding: { top: 20, bottom: 20 },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              lineNumbers: "on",
              renderLineHighlight: "none",
              overviewRulerBorder: false,
              hideCursorInOverviewRuler: true,
              domReadOnly: true,
              contextmenu: false,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProgrammingPostSubmissionView;
